// SCR-STAFF-927 — صف بازبینی دسترسی اضطراری (AP-IAM-04؛ US-IAM-042، AC-IAM-042.1، 042.2): دسترسی‌های اضطراری بازبینی‌نشده با کاربر،
// مراجعه‌کننده، دلیل و اقدامات ثبت‌شده (Audit با همان access_override_id)؛ نتیجه Justified یا Unjustified با توضیح، فقط پس از پایان،
// یک بار و نه برای دسترسی خود (iam.break_glass.review).
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { unwrap, type AccessOverrideView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  DataTable,
  faDigits,
  FormDialog,
  JalaliDate,
  ListPage,
  SelectRHF,
  TextFieldRHF,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { iamKeys } from './common';

export const SCR_STAFF_927 = 'SCR-STAFF-927';
export const BREAK_GLASS_REVIEWS_PATH = '/settings/break-glass-reviews';

const STATE_COLOR: Record<string, 'error' | 'warning' | 'success' | 'default'> = {
  Active: 'error',
  Ended: 'warning',
  Justified: 'success',
  Unjustified: 'error',
};

const reviewSchema = z.object({
  outcome: z.enum(['Justified', 'Unjustified'], { error: 'common:validation.required' }),
  note: zRequiredText(1000),
});

type ReviewForm = z.input<typeof reviewSchema>;

function ReviewDialog({ item, onClose, onSaved }: { item: AccessOverrideView; onClose: () => void; onSaved: () => void }) {
  const { tenantApi } = useTenantSession();
  const [submitError, setSubmitError] = useState<unknown>(undefined);
  const { control, handleSubmit, formState, setError } = useForm<ReviewForm>({
    resolver: zodResolver(reviewSchema),
    defaultValues: { outcome: 'Justified', note: '' },
  });
  const review = useMutation({
    mutationFn: (form: ReviewForm) =>
      unwrap(
        tenantApi.POST('/api/v1/identity/access-overrides/{id}:review', {
          params: { path: { id: item.overrideId } },
          body: { outcome: form.outcome, note: form.note.trim() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSuccess: onSaved,
    onError: (error) => {
      const applied = applyServerErrors(error, setError, { fields: ['outcome', 'note'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });
  const { t } = useTranslation('iam');

  return (
    <FormDialog
      open
      titleKey="iam:break_glass_reviews.review_title"
      screenId="SCR-STAFF-927-REVIEW"
      onSubmit={() => void handleSubmit((form) => review.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={review.isPending}
      submitError={submitError}
      submitKey="iam:break_glass_reviews.review"
    >
      <Typography>
        {item.userName} — {item.patientName}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t(`iam:break_glass.reasons.${item.reasonCode}`, { defaultValue: item.reasonCode })}: {item.reason}
      </Typography>
      <SelectRHF
        control={control}
        name="outcome"
        labelKey="iam:break_glass_reviews.outcome"
        required
        fullWidth
        options={[
          { value: 'Justified', labelKey: 'iam:break_glass_reviews.outcomes.Justified' },
          { value: 'Unjustified', labelKey: 'iam:break_glass_reviews.outcomes.Unjustified' },
        ]}
      />
      <TextFieldRHF
        control={control}
        name="note"
        labelKey="iam:break_glass_reviews.note"
        required
        fullWidth
        multiline
        minRows={3}
        autoFocus
        slotProps={{ htmlInput: { 'data-testid': 'review-note', maxLength: 1000 } }}
      />
      <Alert severity="warning">{t('iam:break_glass_reviews.final_notice')}</Alert>
    </FormDialog>
  );
}

export function BreakGlassReviewPage() {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('iam.break_glass.review');
  const [includeReviewed, setIncludeReviewed] = useState(false);
  const [reviewing, setReviewing] = useState<AccessOverrideView | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const queue = useQuery({
    queryKey: iamKeys.reviewQueue(session?.tenantId, includeReviewed),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/identity/access-overrides/review-queue', { params: { query: { includeReviewed } } })),
  });

  const columns: DataTableColumn<AccessOverrideView>[] = [
    {
      id: 'activated',
      headerKey: 'iam:break_glass_reviews.columns.activated',
      cell: (row) => <JalaliDate value={row.activatedAt} withTime />,
    },
    { id: 'user', headerKey: 'iam:break_glass_reviews.columns.user', cell: (row) => row.userName ?? t('common:states.none') },
    {
      id: 'patient',
      headerKey: 'iam:break_glass_reviews.columns.patient',
      cell: (row) => (
        <span>
          {row.patientName ?? t('common:states.none')}
          {row.patientFileNumber ? ` (${faDigits(row.patientFileNumber)})` : ''}
        </span>
      ),
    },
    {
      id: 'reason',
      headerKey: 'iam:break_glass_reviews.columns.reason',
      cell: (row) => (
        <Stack>
          <span>{t(`iam:break_glass.reasons.${row.reasonCode}`, { defaultValue: row.reasonCode })}</span>
          <Typography variant="body2" color="text.secondary">
            {row.reason}
          </Typography>
        </Stack>
      ),
    },
    {
      id: 'actions',
      headerKey: 'iam:break_glass_reviews.columns.actions',
      cell: (row) => (
        <Tooltip
          title={row.actions
            .map((action) => t(`iam:audit.actions.${action.action.replaceAll('.', '_')}`, { defaultValue: action.action }))
            .join(t('iam:audit.list_separator'))}
        >
          <span data-testid={`review-actions-${row.overrideId}`}>
            {t('iam:break_glass_reviews.actions_count', { value: faDigits(row.actions.length) })}
          </span>
        </Tooltip>
      ),
    },
    {
      id: 'state',
      headerKey: 'iam:break_glass_reviews.columns.state',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            color={STATE_COLOR[row.state] ?? 'default'}
            label={t(`iam:break_glass_reviews.states.${row.state}`)}
            data-testid={`review-state-${row.overrideId}`}
          />
          {row.canReview && write.canWrite ? (
            <Button size="small" onClick={() => setReviewing(row)} data-testid={`review-open-${row.overrideId}`}>
              {t('iam:break_glass_reviews.review')}
            </Button>
          ) : row.state === 'Active' ? (
            <Typography variant="caption" color="text.secondary">
              {t('iam:break_glass_reviews.not_ended')}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
  ];

  return (
    <ListPage screenId={SCR_STAFF_927} titleKey="iam:break_glass_reviews.title" subtitleKey="iam:break_glass_reviews.subtitle">
      <DataTable
        captionKey="iam:break_glass_reviews.title"
        columns={columns}
        rows={queue.data?.items}
        getRowId={(row) => row.overrideId}
        isLoading={queue.isPending}
        error={queue.error}
        onRetry={() => void queue.refetch()}
        emptyTitleKey="iam:break_glass_reviews.empty"
        toolbar={
          <FormControlLabel
            control={
              <Switch checked={includeReviewed} onChange={(event) => setIncludeReviewed(event.target.checked)} data-testid="review-include-reviewed" />
            }
            label={t('iam:break_glass_reviews.include_reviewed')}
          />
        }
      />
      {reviewing && (
        <ReviewDialog
          item={reviewing}
          onClose={() => setReviewing(null)}
          onSaved={() => {
            setReviewing(null);
            setNotice(t('iam:break_glass_reviews.saved'));
            void queryClient.invalidateQueries({ queryKey: ['identity', session?.tenantId, 'access-overrides'] });
          }}
        />
      )}
      <Snackbar open={notice !== null} autoHideDuration={4000} onClose={() => setNotice(null)} message={notice} />
    </ListPage>
  );
}
